import { useEffect, useState } from 'react'
import { type Material } from '@shared/material'
import { choosePdfjsLegacy } from '@/materials/pdf'
import { MaterialError, openPdf, prepareMaterial, type MaterialApi } from '@/materials/prepare'

// The page a headless browser opens when an AI adds a PDF (worker/prepare.ts):
// the same preparation as a person's drop, run where a PDF can be drawn. The
// one-time grant in the address is the only access it has: to that PDF, its
// derived files and registering it.

declare global {
  interface Window {
    __mootPrepared?: { material: Material } | { error: string }
  }
}

function grantApi(token: string): MaterialApi {
  const base = `/api/prepare/${encodeURIComponent(token)}`
  return {
    putDerived: async (name, body) => {
      const res = await fetch(`${base}/derived/${name}`, {
        method: 'PUT',
        headers: { 'content-type': body.type },
        body,
      })
      if (!res.ok) throw new MaterialError('upload_failed')
    },
    register: async (m) => {
      const res = await fetch(`${base}/material`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(m),
      })
      if (!res.ok) throw new MaterialError('register_failed')
      return (await res.json()) as Material
    },
  }
}

/** Anything that breaks the page ends the wait at once, instead of at the server's timeout. */
function failOnUncaught() {
  const fail = (why: unknown) => {
    window.__mootPrepared ??= { error: why instanceof Error ? why.message : String(why) }
  }
  window.addEventListener('error', (e) => {
    fail(e.error ?? e.message)
  })
  window.addEventListener('unhandledrejection', (e) => {
    fail(e.reason)
  })
}

/** Prepared once per page load, even where effects run twice (development). */
let started = false

export function Prepare() {
  const [state, setState] = useState('preparing')
  useEffect(() => {
    if (started) return
    started = true
    failOnUncaught()
    choosePdfjsLegacy()
    const token = location.hash.slice(1)
    void (async () => {
      try {
        const res = await fetch(`/api/prepare/${encodeURIComponent(token)}/file`)
        if (!res.ok) throw new MaterialError('no_grant')
        const name = decodeURIComponent(res.headers.get('x-filename') ?? 'document.pdf')
        const doc = await openPdf(new Uint8Array(await res.arrayBuffer()))
        try {
          const material = await prepareMaterial(doc, name, grantApi(token))
          window.__mootPrepared = { material }
          setState('done')
        } finally {
          void doc.loadingTask.destroy()
        }
      } catch (e) {
        const error = e instanceof Error ? e.message : 'failed'
        window.__mootPrepared = { error }
        setState(error)
      }
    })()
  }, [])
  return <p data-state={state}>{state}</p>
}
